import { F } from '@/utils/intl';
import { EyeInvisibleOutlined, HolderOutlined } from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import { Spin } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLastGoodRender } from '../lastGoodRender';
import { TwigPreviewResult, renderTwigPreview } from '../twigRenderer';
import {
  CANVAS_STAMP_ATTRIBUTE,
  blankEmptyImageSources,
  canvasStamp,
  editorCanvasCss,
  emptySlotKeys,
  injectEditorStyles,
  preparePreviewHtml,
} from './canvasStyles';
import { BLOCK_ID_ATTRIBUTE, SLOT_ATTRIBUTE } from './compile';
import {
  bodyTextSurvived,
  diagnoseContentDrop,
  expectedMarkers,
} from './contentDrop';
import { EmailDocument } from './document';
import { BLOCK_DRAG_PREFIX } from './dragdrop';
import {
  CanvasMeasurements,
  CommentMarker,
  DropTarget,
  Point,
  RawMeasurement,
  Rect,
  blockAtPoint,
  collectMeasurements,
  containsPoint,
  dropTargetAtPoint,
  layoutBands,
  pairCommentMarkers,
  parseCommentMarker,
} from './geometry';

import styles from './CanvasFrame.less';

/**
 * How long the canvas lags the document while editing.
 *
 * The debounce is on the **Twig render**, not on the document or the compile:
 * `compile()` is string building, but `renderTwigPreview` parses and executes real
 * Twig through twig.js with every fragment registered, which is far too much to do
 * per keystroke. Everything else — the inspector, the structure tree, undo — stays
 * immediate, so only the preview trails.
 */
export const CANVAS_RENDER_DEBOUNCE_MS = 120;

/** Stands in for a render that has not been attempted yet (catalog still loading). */
const EMPTY_RENDER: TwigPreviewResult = {
  html: '',
  placeholderVariables: [],
  conditionVariables: [],
};

/**
 * The grip that starts a drag of an existing block.
 *
 * A separate handle rather than making the block itself draggable: the overlay
 * also has to serve click-to-select and (from Sprint 3.4) text editing, and a
 * whole-block drag would fight both.
 */
const BlockDragHandle: React.FC<{
  blockId: string;
  rect: Rect;
  /** The block's name, so the grip says *what* it is about to move. */
  label?: string | null;
}> = ({ blockId, rect, label }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${BLOCK_DRAG_PREFIX}${blockId}`,
    data: { kind: 'block', blockId },
  });

  return (
    <div
      ref={setNodeRef}
      className={styles.dragHandle}
      // Measured geometry, not styling: the position comes from the block's rect
      // inside the canvas iframe, so it cannot live in the stylesheet.
      style={{ top: rect.top, left: rect.left + rect.width }}
      data-dragging={isDragging ? '1' : undefined}
      {...attributes}
      {...listeners}
    >
      <HolderOutlined />
      {/* Named, because a grip on a partner partial is otherwise unattributed: the
          blocks it sits on are whole bands of someone else's markup, and which one you
          are about to drag is not obvious from the outline alone. */}
      {label && <span className={styles.dragHandleLabel}>{label}</span>}
    </div>
  );
};

/**
 * Rects for blocks the compiler bracketed with comment markers — every partner partial
 * and every raw-HTML block, none of which render `data-cvn-id`.
 *
 * Without this they have no geometry, and no geometry means no hover outline, no
 * selection, no drag handle, and no dropping *next to* them either (the drop resolver
 * filters out siblings it cannot measure). They were tree-only for exactly this reason.
 *
 * Measured with a `Range` between the two comments rather than by walking siblings: the
 * content can be nested differently from the markers, and a Range spans that correctly
 * where a sibling walk would find the wrong node. **Measured, not assumed** — two marked
 * rows in one `<table>` land with the opening comment in the `<table>` and the rest in
 * the implied `<tbody>`, and the two Ranges still report 40 px and 80 px rather than
 * both collapsing onto the whole `<tbody>`.
 */
function measureMarkedBlocks(frameDocument: Document): RawMeasurement[] {
  const comments: (CommentMarker & { node: Comment; index: number })[] = [];
  const walker = frameDocument.createNodeIterator(
    frameDocument.body,
    NodeFilter.SHOW_COMMENT,
  );

  let index = 0;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const marker = parseCommentMarker(node.nodeValue ?? '');
    if (marker) {
      comments.push({ ...marker, node: node as Comment, index });
    }
    index += 1;
  }

  const measurements: RawMeasurement[] = [];
  for (const pair of pairCommentMarkers(comments)) {
    const start = comments.find((entry) => entry.index === pair.start)?.node;
    const end = comments.find((entry) => entry.index === pair.end)?.node;
    if (!start || !end) continue;

    try {
      const range = frameDocument.createRange();
      range.setStartAfter(start);
      range.setEndBefore(end);
      const box = range.getBoundingClientRect();
      measurements.push({
        blockId: pair.blockId,
        rect: {
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
        },
      });
    } catch {
      // A hand edit can leave the two markers in unrelated subtrees, which makes the
      // range invalid. One block falls back to tree-only selection; the canvas is fine.
    }
  }

  return measurements;
}

/**
 * The chrome drawn around the email at a given simulated width.
 *
 * Cosmetic only — the frame is a sibling wrapper, never a parent of `.canvasStage`,
 * because the selection overlay is positioned against the stage and every measured
 * rect is expressed relative to it. Putting padding *inside* that box would move
 * every block's geometry out from under the overlay.
 */
export type DeviceKind = 'phone' | 'tablet' | 'desktop';

interface CanvasFrameProps {
  /** Compiled body — the real Twig the server would render. */
  html: string;
  document: EmailDocument;
  previewContext?: Record<string, string>;
  translations?: Record<string, string>;
  fragments?: Record<string, string>;
  layoutKey?: string | null;
  /** Simulated device width in px; drives the email's own media queries. */
  width?: number;
  /** Which device chrome to draw around it. Omitted ⇒ none. */
  device?: DeviceKind;
  selectedBlockId?: string | null;
  onSelectBlock?: (blockId: string | null) => void;
  /** Reports fresh geometry so drag-and-drop can share one measurement pass. */
  onMeasure?: (measurements: CanvasMeasurements) => void;
  /**
   * Pointer position in **viewport** coordinates while a drag is in flight, or
   * null when nothing is being dragged. The canvas owns the conversion into
   * overlay space because it owns the overlay element and the measurements.
   */
  dragPoint?: Point | null;
  /** The block being dragged, so it and its subtree are excluded as targets. */
  draggingBlockId?: string | null;
  /** Where a drop would land right now, or null when the pointer is off-canvas. */
  onDropTargetChange?: (target: DropTarget | null) => void;
  /** The selected block's name, shown on its drag grip. */
  selectedLabel?: string | null;
  /** Blocks that only appear under a condition — marked so they look conditional. */
  conditionalBlocks?: ReadonlySet<string>;
  /**
   * Right-click on a block: the id, and the viewport point to open a menu at.
   * The canvas reports rather than renders, because the menu's actions are the
   * editor's (delete, duplicate) and it owns the dispatcher.
   */
  onContextMenu?: (blockId: string, at: { x: number; y: number }) => void;
  /**
   * Editing chrome off: no overlay, no selection, and none of the editor-only CSS
   * that makes empty slots targetable.
   *
   * Preview is deliberately **not** a second surface — it is this frame with the
   * chrome removed, so there is still exactly one renderer and nothing can drift
   * between what you edit and what you preview.
   */
  previewOnly?: boolean;
  /**
   * A notice bar above the canvas already names a layout problem, so the frame stays
   * quiet about content the layout discarded.
   *
   * One root cause, one message: `preflight`'s missing-layout / not-a-layout /
   * slot-not-declared errors are this failure seen *at* its cause, with the layout key
   * in hand. Repeating it under the frame as an outcome said the same thing twice, in
   * vaguer words.
   */
  layoutIssueReported?: boolean;
}

/**
 * The editing surface: the *real* compiled email in an iframe, with an overlay
 * for selection chrome.
 *
 * Nothing here re-implements a block as a React component. That is the point —
 * what you edit is byte-identical to what gets sent, so WYSIWYG can't drift.
 * The cost is that selection has no component tree to interrogate: it works from
 * measured rects, interpreted by the pure helpers in `geometry.ts`.
 *
 * Two non-obvious constraints, both verified in a browser rather than assumed:
 *
 * 1. **`sandbox="allow-same-origin"`, not `sandbox=""`.** An empty sandbox puts
 *    the frame in an opaque origin and `contentDocument` throws `SecurityError`,
 *    leaving nothing to measure. `allow-same-origin` alone is still script-free —
 *    the sandbox escape needs `allow-scripts` *with* it — so email HTML stays
 *    inert while the parent can read geometry. `PreviewPane` keeps `sandbox=""`
 *    because it never measures.
 * 2. **The iframe is sized to its content and never scrolls internally.** That
 *    keeps `getBoundingClientRect()` inside the frame equal to overlay
 *    coordinates, so there is no scroll offset to track. The outer container
 *    scrolls instead.
 */
const CanvasFrame: React.FC<CanvasFrameProps> = ({
  html,
  document: emailDocument,
  previewContext,
  translations,
  fragments,
  layoutKey,
  width = 600,
  device,
  selectedBlockId,
  onSelectBlock,
  onMeasure,
  dragPoint,
  draggingBlockId,
  onDropTargetChange,
  selectedLabel,
  conditionalBlocks,
  onContextMenu,
  previewOnly = false,
  layoutIssueReported = false,
}) => {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [measurements, setMeasurements] = useState<CanvasMeasurements>({
    blocks: [],
  });
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);
  /** The layout band under the pointer, when it is not over a block. */
  const [hoveredLayoutBand, setHoveredLayoutBand] = useState<Rect | null>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const [dropIndicator, setDropIndicator] = useState<DropTarget | null>(null);
  /**
   * Which `srcDoc` the rects in `measurements` describe.
   *
   * Between a document change and the iframe re-rendering, the rects still describe the
   * *previous* layout — so an outline drawn from them lands where the block used to be.
   * That is what a moved block looked like: the block was in its new place and the
   * outline was left behind. Chrome is suppressed until the measurement catches up.
   */
  const [measuredFor, setMeasuredFor] = useState<string | null>(null);
  /**
   * The device width the current rects were taken at.
   *
   * Changing the simulated width re-lays-out the email *inside* the iframe — its own
   * media queries re-run — and that takes a frame. Measuring synchronously read the
   * **previous** layout, so switching to 360 px drew outlines sized for 600 px: they
   * spilled outside the phone frame while the text inside it was clipped. Exactly what
   * "the mobile preview is broken and overflown" looks like.
   */
  const [measuredWidth, setMeasuredWidth] = useState(width);

  /**
   * The body actually rendered: `html` is adopted immediately on mount (so opening
   * a template paints at once) and debounced on every later change.
   */
  const [renderedHtml, setRenderedHtml] = useState(html);
  const lastRenderRef = useRef(0);
  useEffect(() => {
    if (renderedHtml === html) return undefined;

    /**
     * **Leading edge, not a plain trailing debounce.** A drop, an insert or a delete is
     * one discrete action, and making it wait out a delay meant for *typing* is what
     * made a dropped block appear not to move. Typing still coalesces, because the
     * second keystroke arrives inside the window and only the trailing render survives.
     */
    const since = Date.now() - lastRenderRef.current;
    if (since >= CANVAS_RENDER_DEBOUNCE_MS) {
      lastRenderRef.current = Date.now();
      setRenderedHtml(html);
      return undefined;
    }

    const timer = setTimeout(() => {
      lastRenderRef.current = Date.now();
      setRenderedHtml(html);
    }, CANVAS_RENDER_DEBOUNCE_MS - since);
    return () => clearTimeout(timer);
  }, [html, renderedHtml]);

  /**
   * **The fragment catalog is fetched, so on a cold open it is not there yet.**
   *
   * Rendering anyway meant twig.js was asked to `{% extends 'layout/xxxl/base' %}` a
   * template it had never been given — it throws *"Cannot extend an inline template"*,
   * and the canvas showed 4 KB of failure next to "6 problems would stop this email from
   * rendering" until the catalog landed a second later. Reproduced on 2 of 4 cold opens.
   *
   * Emptiness is the signal, not a missing key: the catalog arrives all at once, so an
   * empty map means "not loaded". A key missing from a *loaded* catalog is a real error
   * and must still surface — that one is a deleted building block, not a slow request.
   */
  const catalogPending = !fragments || Object.keys(fragments).length === 0;

  const fresh = useMemo(
    () =>
      catalogPending
        ? EMPTY_RENDER
        : renderTwigPreview(renderedHtml || '', {
            context: previewContext,
            translations,
            fragments,
            layoutKey,
          }),
    [
      catalogPending,
      renderedHtml,
      previewContext,
      translations,
      fragments,
      layoutKey,
    ],
  );

  /**
   * **A failed render does not reach the canvas.** Typing `{{` to open the variable
   * list makes the body unparseable for as long as it takes to finish the expression,
   * and the fallback for a compiled document is a layout-less, marker-less page — so
   * the canvas emptied under the cursor. The previous render stays instead; see
   * `lastGoodRender.ts`.
   */
  const rendered = useLastGoodRender(fresh);

  /**
   * The rendered email plus editor-only CSS. The email itself is never modified —
   * see `canvasStyles.ts` for why an empty slot needs a height here and not in the
   * component.
   */
  const srcDoc = useMemo(() => {
    // `src=""` resolves to this page's URL, so it has to go before the iframe sees it.
    const html = blankEmptyImageSources(rendered.html);
    return previewOnly
      ? preparePreviewHtml(html)
      : injectEditorStyles(
          html,
          editorCanvasCss(emptySlotKeys(emailDocument.blocks)),
        );
  }, [previewOnly, rendered.html, emailDocument]);
  const error = rendered.error;

  /**
   * A render that threw nothing but did not deliver the body's content — see
   * `contentDrop.ts` for what is being compared and why a presence check was not it.
   *
   * Nothing is claimed while the fragment catalog is loading or after a render error:
   * both mean the render is not evidence of anything yet.
   */
  const diagnosable =
    !catalogPending && !rendered.error && emailDocument.blocks.length > 0;

  /**
   * The text probe, for the one case with no markers to compare: a contained
   * (`rawPassthrough`) or detached body **inside a layout**, where the layout is the only
   * thing that could have swallowed it. Rendering the body a second time without its
   * layout is what makes the comparison possible, and it is gated to this case so the
   * common path pays nothing for it.
   */
  const markerCount = useMemo(
    () => expectedMarkers(renderedHtml || '').length,
    [renderedHtml],
  );
  const needsTextProbe = diagnosable && Boolean(layoutKey) && markerCount === 0;

  const unlayouted = useMemo(
    () =>
      needsTextProbe
        ? renderTwigPreview(renderedHtml || '', {
            context: previewContext,
            translations,
            fragments,
            layoutKey: null,
          })
        : null,
    [needsTextProbe, renderedHtml, previewContext, translations, fragments],
  );

  const dropVerdict = useMemo(() => {
    if (!diagnosable) return { kind: 'ok' } as const;
    return diagnoseContentDrop({
      body: renderedHtml || '',
      html: rendered.html,
      hasContent: emailDocument.blocks.length > 0,
      layouted: Boolean(layoutKey),
      unmarkedProbe:
        unlayouted && !unlayouted.error
          ? bodyTextSurvived(unlayouted.html, rendered.html)
          : null,
    });
  }, [diagnosable, renderedHtml, rendered.html, layoutKey, unlayouted]);

  /** Read inside `measure`, which must not change identity when the width does. */
  const widthRef = useRef(width);
  widthRef.current = width;

  /**
   * True while the canvas is behind the document: either the render is still pending, or
   * the iframe has new markup that has not been measured yet. Selection chrome is drawn
   * from measured rects, so drawing it now would put it where the block *was*.
   */
  const chromeStale =
    renderedHtml !== html ||
    measuredFor !== canvasStamp(rendered.html) ||
    measuredWidth !== width;

  /**
   * Reads every marker in the frame and republishes geometry.
   *
   * Also syncs the frame's height to its content — the two are done together
   * because a height change reflows the content, which invalidates the rects
   * that were just taken.
   */
  const measure = useCallback(() => {
    const frame = frameRef.current;
    const frameDocument = frame?.contentDocument;
    if (!frame || !frameDocument?.body) return;

    const contentHeight = frameDocument.documentElement.scrollHeight;
    setFrameHeight((current) =>
      Math.abs(current - contentHeight) > 1 ? contentHeight : current,
    );

    const selector = `[${BLOCK_ID_ATTRIBUTE}],[${SLOT_ATTRIBUTE}]`;
    const raw: RawMeasurement[] = [];
    frameDocument.querySelectorAll(selector).forEach((element) => {
      const box = element.getBoundingClientRect();
      raw.push({
        blockId: element.getAttribute(BLOCK_ID_ATTRIBUTE),
        slotMarker: element.getAttribute(SLOT_ATTRIBUTE),
        rect: {
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
        },
      });
    });

    raw.push(...measureMarkedBlocks(frameDocument));

    const bodyBox = frameDocument.body.getBoundingClientRect();
    const next = collectMeasurements(
      raw,
      { x: 0, y: 0 },
      {
        top: bodyBox.top,
        left: bodyBox.left,
        width: bodyBox.width,
        height: bodyBox.height,
      },
    );

    setMeasurements(next);
    // What the iframe *has*, read from the document itself — not what it was last handed.
    setMeasuredFor(
      frameDocument
        .querySelector(`[${CANVAS_STAMP_ATTRIBUTE}]`)
        ?.getAttribute(CANVAS_STAMP_ATTRIBUTE) ?? null,
    );
    setMeasuredWidth(frame.clientWidth || widthRef.current);
    onMeasure?.(next);
  }, [onMeasure]);

  // Re-measure on load, on any content reflow, and once fonts/images settle —
  // an image that arrives late shifts every rect below it.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    let observer: ResizeObserver | null = null;

    const attach = () => {
      const frameDocument = frame.contentDocument;
      if (!frameDocument?.body) return;

      measure();

      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => measure());
        observer.observe(frameDocument.body);
      }

      frameDocument.querySelectorAll('img').forEach((image) => {
        if (!(image as HTMLImageElement).complete) {
          image.addEventListener('load', measure, { once: true });
          image.addEventListener('error', measure, { once: true });
        }
      });
    };

    frame.addEventListener('load', attach);
    // srcDoc may already be committed when this effect runs.
    if (frame.contentDocument?.readyState === 'complete') attach();

    return () => {
      frame.removeEventListener('load', attach);
      observer?.disconnect();
    };
  }, [srcDoc, measure]);

  /**
   * A width change re-runs the email's own media queries, so every rect moves — and the
   * reflow happens *after* this effect. Two frames, because the first only guarantees the
   * new box; the second sees the layout that resulted from it.
   */
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => measure());
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [width, measure]);

  const pointFromEvent = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): Point => {
      const box = event.currentTarget.getBoundingClientRect();
      return { x: event.clientX - box.left, y: event.clientY - box.top };
    },
    [],
  );

  /**
   * Resolves the in-flight pointer position into a drop target.
   *
   * Gated on the pointer actually being over the overlay: `dropTargetAtPoint`
   * answers for *any* point — a pointer far outside still resolves to the document
   * root — so without this check, releasing a palette item over the inspector
   * would silently insert at the end of the email.
   */
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!dragPoint || !overlay) {
      setDropIndicator((current) => (current === null ? current : null));
      onDropTargetChange?.(null);
      return;
    }

    const box = overlay.getBoundingClientRect();
    const local: Point = {
      x: dragPoint.x - box.left,
      y: dragPoint.y - box.top,
    };
    const inside = containsPoint(
      { top: 0, left: 0, width: box.width, height: box.height },
      local,
    );

    const target = inside
      ? dropTargetAtPoint(measurements, emailDocument, local, {
          draggingId: draggingBlockId ?? undefined,
        })
      : null;

    setDropIndicator(target);
    onDropTargetChange?.(target);
  }, [
    dragPoint,
    measurements,
    emailDocument,
    draggingBlockId,
    onDropTargetChange,
  ]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onSelectBlock?.(
        blockAtPoint(measurements, emailDocument, pointFromEvent(event)),
      );
    },
    [measurements, emailDocument, onSelectBlock, pointFromEvent],
  );

  /**
   * Right-click selects the block under the pointer *and* reports where the menu
   * should open. Selecting first is what makes the menu's actions unambiguous —
   * they act on the selection, so the two must not disagree.
   */
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onContextMenu) return;
      const blockId = blockAtPoint(
        measurements,
        emailDocument,
        pointFromEvent(event),
      );
      if (!blockId) return;
      event.preventDefault();
      onSelectBlock?.(blockId);
      onContextMenu(blockId, { x: event.clientX, y: event.clientY });
    },
    [measurements, emailDocument, onSelectBlock, onContextMenu, pointFromEvent],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const point = pointFromEvent(event);
      const next = blockAtPoint(measurements, emailDocument, point);
      setHoveredBlockId((current) => (current === next ? current : next));

      /**
       * Over the layout's own chrome — the bands above and below the content.
       *
       * Only when no block is under the pointer: a block sitting inside a band
       * is still the thing you are pointing at.
       */
      const band = next
        ? null
        : layoutBands(measurements).find((rect) =>
            containsPoint(rect, point),
          ) ?? null;
      setHoveredLayoutBand((current) =>
        current?.top === band?.top && current?.height === band?.height
          ? current
          : band,
      );
    },
    [measurements, emailDocument, pointFromEvent],
  );

  const rectFor = useCallback(
    (blockId: string | null | undefined) =>
      blockId
        ? measurements.blocks.find((entry) => entry.blockId === blockId)?.rect
        : undefined,
    [measurements],
  );

  const selectedRect = rectFor(selectedBlockId);
  const hoveredRect =
    hoveredBlockId && hoveredBlockId !== selectedBlockId
      ? rectFor(hoveredBlockId)
      : undefined;

  const toStyle = (rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  }) => ({
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  });

  if (catalogPending) {
    return (
      <div className={styles.canvasScroll}>
        <div className={styles.canvasLoading}>
          <Spin size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.canvasScroll}>
      {/* The clip lives here, not on the stage: `overflow` on the stage forces *both*
          axes, which crops the email to its own box and leaves nothing to scroll. */}
      {/* Purely decorative shell. It wraps the clip, so the stage inside keeps its
          own box and the overlay's coordinate space is untouched. */}
      <div className={styles.deviceShell} data-device={device}>
        {device === 'phone' && <span className={styles.deviceSpeaker} />}
        {device === 'desktop' && (
          <span className={styles.deviceBar}>
            <i />
            <i />
            <i />
          </span>
        )}
        <div className={styles.canvasClip}>
          <div className={styles.canvasStage} style={{ width }}>
            <iframe
              ref={frameRef}
              className={styles.canvasFrame}
              title="email-canvas"
              // See the note above: measurable, still script-free.
              sandbox="allow-same-origin"
              srcDoc={srcDoc}
              style={{ height: frameHeight || undefined }}
              scrolling="no"
            />

            {/* Sits above the frame and takes every pointer event: the canvas is an
            editing surface, not a browsable page. Absent in preview, where the email
            is exactly that — something to look at. */}
            {!previewOnly && (
              <div
                ref={overlayRef}
                className={styles.overlay}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => {
                  setHoveredBlockId(null);
                  setHoveredLayoutBand(null);
                }}
                role="presentation"
              >
                {hoveredRect && !chromeStale && (
                  <div
                    className={styles.hoverOutline}
                    style={toStyle(hoveredRect)}
                  />
                )}

                {/* Says what it is rather than just refusing to highlight: without this,
              "the layout is not editable here" and "selection is broken" look the
              same from the pointer's side. */}
                {hoveredLayoutBand && !chromeStale && (
                  <div
                    className={styles.layoutBand}
                    style={toStyle(hoveredLayoutBand)}
                  >
                    <span className={styles.layoutBandLabel}>
                      <F
                        id="pages.emailCenter.visual.layoutBand"
                        defaultMessage="Layout — edit it in the Theme tab"
                      />
                    </span>
                  </div>
                )}
                {selectedRect && !chromeStale && (
                  <div
                    className={styles.selectedOutline}
                    style={toStyle(selectedRect)}
                  />
                )}

                {/* A block that only sometimes appears, marked where it sits. Drawn from the
              same measured rects as everything else, and skipped while they are stale. */}
                {!chromeStale &&
                  measurements.blocks
                    .filter((entry) => conditionalBlocks?.has(entry.blockId))
                    .map((entry) => (
                      <div
                        key={entry.blockId}
                        className={styles.conditionalOutline}
                        style={toStyle(entry.rect)}
                      >
                        <span className={styles.conditionalBadge}>
                          <EyeInvisibleOutlined />
                        </span>
                      </div>
                    ))}

                {/* Where the block would land. A line between siblings, a box for an
              empty slot — the distinction comes from `dropTargetAtPoint`. */}
                {dropIndicator && (
                  <div
                    className={
                      dropIndicator.indicator.type === 'line'
                        ? styles.dropLine
                        : styles.dropBox
                    }
                    style={toStyle(dropIndicator.indicator.rect)}
                  />
                )}

                {/* Only for the selection: a handle on every hovered block would turn
              the canvas into a field of grips.

              Deliberately **not** unmounted while a drag is in flight, which it used
              to be: unmounting the active draggable mid-gesture leaves dnd-kit
              holding a node that no longer exists, and it left a block move with
              nothing on screen at all. It dims instead (`data-dragging`), and the
              drag chip is what follows the cursor. */}
                {selectedBlockId && selectedRect && !chromeStale && (
                  <BlockDragHandle
                    blockId={selectedBlockId}
                    rect={selectedRect}
                    label={selectedLabel}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/**
       * A render failure said only what twig.js said — "TwigException:
       * Twig.expression.type.variable cannot follow a Twig.expression.type.string
       * at template:35 near 'word...'" — as the whole message, in English, in a
       * partner's German editor. The same split `issueMessages.ts` uses applies
       * here: a translated sentence leads, the engine's own words stay underneath
       * as technical detail. Kept in the DOM rather than moved to a `title`,
       * because a tooltip is exactly what B10 and B11 were filed about.
       */}
      {error && (
        /**
         * Neutral while the canvas is merely *held*: an unfinished `{{ ` is a normal
         * moment in typing a variable, not a fault to fix, and a red bar telling the
         * author their email is broken every time they open the variable list is the
         * same interruption the frozen canvas was meant to remove.
         */
        <div
          className={rendered.stale ? styles.renderNotice : styles.renderError}
        >
          {rendered.stale ? (
            <F
              id="pages.emailCenter.visual.renderPaused"
              defaultMessage="The code is unfinished, so the preview below is the last version that rendered. It updates again as soon as the code is valid."
            />
          ) : (
            <F
              id="pages.emailCenter.visual.renderFailed"
              defaultMessage="This email could not be rendered, so the preview below is only an approximation. There is a problem in its code."
            />
          )}
          <span className={styles.renderErrorDetail}>{error}</span>
        </div>
      )}

      {/* Each verdict says what happened, because "content is not appearing" left the
          reader to guess between four different situations. The layout cases stay quiet
          when a notice bar upstairs already names the layout — see
          `layoutIssueReported`. */}
      {!error && dropVerdict.kind === 'dropped' && !layoutIssueReported && (
        <div className={styles.renderError}>
          {dropVerdict.layouted ? (
            <F
              id="pages.emailCenter.visual.contentDroppedByLayout"
              defaultMessage="The layout does not show this email's content, so it would arrive empty."
            />
          ) : (
            <F
              id="pages.emailCenter.visual.contentDroppedNoLayout"
              defaultMessage="This email's content is discarded while rendering, so it would arrive empty."
            />
          )}
        </div>
      )}

      {!error && dropVerdict.kind === 'partial' && !layoutIssueReported && (
        <div className={styles.renderError}>
          <F
            id="pages.emailCenter.visual.contentDroppedPartial"
            defaultMessage="{count, plural, one {# block does not} other {# blocks do not}} appear in the sent email."
            values={{ count: dropVerdict.count }}
          />
        </div>
      )}

      {/* Not a fault: the sample data simply does not match any of the conditions. A
          statement, styled as one. */}
      {!error && dropVerdict.kind === 'empty-by-condition' && (
        <div className={styles.renderNotice}>
          <F
            id="pages.emailCenter.visual.renderedEmptyByCondition"
            defaultMessage="With this preview data, this email shows no content — every block has a condition that does not match."
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(CanvasFrame);
